import i18next from 'i18next';
import { cloneDeep, get, last, merge, omit } from 'lodash';
import moment from 'moment';
import { priorities } from '@/models/priority';
import createBackendStore from '@/lib/create-backend-store';
import {
  resolve,
  translateProperty,
  translateValue,
} from '@/lib/util';
import store from '@/store';

const columns = {};

function makeCaption(entity, property, caption, description) {
  if (caption) {
    return resolve(caption, description);
  }

  const context = description.captionContext;

  return translateProperty(entity, property, { context });
}

export function buildColumn(entity, property, description) {
  const propertySegments = property.split('.');
  const entityProperty = last(propertySegments);
  const defaultConfig = resolve(columns[entity][entityProperty], description);
  const config = merge({}, defaultConfig, description);
  const caption = makeCaption(entity, entityProperty, config.caption, description);

  if (defaultConfig.dataType === 'date') {
    config.format ??= { year: '2-digit', month: '2-digit', day: '2-digit' };
    config.alignment ??= 'right';
  }

  const column = {};

  if (config.calculateCellValue) {
    column.name = property;
  } else {
    column.dataField = property;
  }

  Object.assign(column, omit(config, ['entity', 'property', 'options', 'caption', 'captionContext', 'valueContext']));

  column.caption = caption;

  return column;
}

export function buildColumns(defaultEntity, descriptions) {
  return descriptions.map((description) => {
    const entity = description.entity || defaultEntity;
    return buildColumn.call(this, entity, description.property, description);
  });
}

function configureSetElementColumn(entity, property, description, options = {}) {
  const { property: columnProperty, valueContext } = description;
  const { ignoreMissingTranslation } = options;

  const sortValuePath = columnProperty.split('.').slice(0, -1);
  sortValuePath.push(property, 'name');

  return {
    dataType: 'number',
    lookup: {
      dataSource: {
        store: store.state.setElements,
        postProcess(rows) {
          // Note that there is a special 'null' column indicating the 'all' filter.

          const clonedRows = cloneDeep(
            rows.filter(row => row === null || row.set === `entities.${entity}.values.${property}`),
          );

          clonedRows.forEach((row) => {
            if (!row) return;

            // eslint-disable-next-line no-param-reassign
            row.translatedName = translateValue(entity, property, row.name, {
              context: valueContext, ignoreMissingTranslation,
            });
          });

          // Sorts rows by translated name but also puts the 'null' row in front.
          return clonedRows.sort((lhsRow, rhsRow) => {
            const a = get(lhsRow, 'translatedName');
            const b = get(rhsRow, 'translatedName');

            if (a === b) return 0;
            if (a === undefined) return -1;
            if (b === undefined) return 1;

            return a < b ? -1 : 1;
          });
        },
      },
      valueExpr: 'id',
      displayExpr(row) {
        const { translatedName } = row;

        if (translatedName) return translatedName;

        return translateValue(entity, property, row.name, {
          context: valueContext, ignoreMissingTranslation,
        });
      },
    },
    calculateSortValue: sortValuePath.join('.'),
  };
}

const common = {
  manufacturerId: {
    dataType: 'number',
    caption: () => i18next.t('entities.manufacturer.name'),
    lookup: {
      dataSource: createBackendStore('/manufacturers', { fields: ['name'] }),
      valueExpr: 'id',
      displayExpr: 'name',
    },
    calculateSortValue: 'manufacturer.name',
  },
  'statusMap[:id]': ({ options, captionContext }) => {
    const { status } = options;

    return {
      dataField: `statusMap[${status.id}]`,
      dataType: 'boolean',
      caption: () => i18next.t(`entities.status.values.station.${status.station}`, { context: captionContext }),
    };
  },
};

Object.assign(columns, {
  locale: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    namespace: {
      dataType: 'string',
    },
    key: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    language: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    translation: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
  },
  manufacturer: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    name: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    'statusMap[:id]': common['statusMap[:id]'],
  },
  manufacturingItem: {
    id: {
      dataType: 'number',
      allowEditing: false,
      customizeText({ value }) {
        return `MI-${value}`;
      },
    },
    complaintTypeId: description => ({
      ...configureSetElementColumn('manufacturingItem', 'complaintType', description),
      editorOptions: {
        showClearButton: true,
      },
    }),
    deliverToId: description => ({
      ...configureSetElementColumn('orderItem', 'deliverTo', description),
      editorOptions: {
        showClearButton: true,
      },
    }),
    lamellaTypeId: description => ({
      ...configureSetElementColumn('orderItem', 'lamellaType', description),
      editorOptions: {
        showClearButton: true,
      },
    }),
    legProfile: {
      dataType: 'string',
    },
    manufacturerId: common.manufacturerId,
    manufacturingDefect: {
      dataType: 'boolean',
    },
    orderedAt: {
      dataType: 'date',
    },
    orderItemEid: {
      dataType: 'number',
    },
    materialId: description => ({
      ...configureSetElementColumn('orderItem', 'material', description),
      editorOptions: {
        showClearButton: true,
      },
    }),
    packageCount: {
      dataType: 'number',
    },
    planWeek: {
      dataType: 'number',
      dataField: 'planWeek',
      calculateCellValue(rowData) {
        const { planWeek } = rowData;

        return planWeek && moment(planWeek).isoWeek();
      },
      calculateFilterExpression(filterValue, selectedFilterOperation) {
        const splitName = this.name.split('.');
        const selector = [...splitName.slice(0, -1), 'planWeek'].join('.');

        if (filterValue === null) return [selector, selectedFilterOperation, null];

        // January = 0, December = 11
        const weeksInYear = moment().month(11).date(31).isoWeek(); // 52 or 53
        const window = weeksInYear / 2;
        const currentWeek = moment().isoWeek();
        const cutoffWeek = (currentWeek + window) % weeksInYear;
        const baseYear = new Date().getFullYear() + (currentWeek < window ? -1 : 0);

        function yearOf(week) {
          // We assume the given week is within the window
          // between the past and future 26 weeks.
          return baseYear + (week <= cutoffWeek ? 1 : 0);
        }

        function beginOf(week) {
          return moment()
            .year(yearOf(week))
            .isoWeek(week)
            .isoWeekday(1)
            .format('YYYY-MM-DD');
        }

        function endOf(week) {
          return moment()
            .year(yearOf(week))
            .isoWeek(week)
            .isoWeekday(7)
            .format('YYYY-MM-DD');
        }

        function expressionFor(week) {
          return [
            [selector, '>=', beginOf(week)],
            [selector, '<=', endOf(week)],
          ];
        }

        function anyOf(weeks) {
          const expression = Array(2 * weeks.length - 1);

          expression[0] = expressionFor(weeks[0]);

          for (let i = 1; i < weeks.length; ++i) {
            expression[2 * i - 1] = 'or';
            expression[2 * i] = expressionFor(weeks[i]);
          }

          return expression;
        }

        switch (selectedFilterOperation) {
          case '=':
            return expressionFor(filterValue);
          case '<>':
            return ['!', expressionFor(filterValue)];
          case '>':
            return [selector, '>', endOf(filterValue)];
          case '<':
            return [selector, '<', beginOf(filterValue)];
          case '>=':
            return [selector, '>=', beginOf(filterValue)];
          case '<=':
            return [selector, '<=', endOf(filterValue)];
          case 'between': {
            const [week1, week2] = filterValue;

            if (!week1 || !week2) return null;

            // week1 && week2
            return [
              [selector, '>=', beginOf(week1)],
              [selector, '<=', endOf(week2)],
            ];
          }
          case 'anyof':
            return anyOf(filterValue);
          case 'noneof':
            return ['!', anyOf(filterValue)];
          default:
            console.error(`Unexpected filter operation: ${selectedFilterOperation}`);
            return [];
        }
      },
    },
    plateThickness: {
      dataType: 'number',
    },
    productCategoryId: description => configureSetElementColumn(
      'orderItem', 'productCategory', description,
    ),
    productNameId: description => configureSetElementColumn(
      'orderItem', 'productName', description, { ignoreMissingTranslation: true },
    ),
    priority: {
      dataType: 'number',
      lookup: {
        dataSource: {
          store: {
            type: 'array',
            data: priorities.map(value => ({ value })),
            key: 'value',
          },
        },
        valueExpr: 'value',
        displayExpr({ value }) {
          return translateValue('manufacturingItem', 'priority', value);
        },
      },
    },
    quantity: {
      dataType: 'number',
      validationRules: [
        { type: 'required' },
      ],
    },
    statusId: () => ({
      dataType: 'number',
      caption: i18next.t('entities.status.name'),
      lookup: {
        dataSource: {
          store: {
            type: 'array',
            data: store.state.statuses.map(status => Object.create(status, {
              translatedName: { value: translateValue('status', 'name', status.name) },
            })),
            key: 'id',
          },
        },
        valueExpr: 'id',
        displayExpr: 'translatedName',
      },
      calculateSortValue: 'status.position',
    }),
    statusEventMap: ({ options = {} }) => ({
      dataField: undefined,
      caption: () => i18next.t('entities.manufacturingItem.misc.statusEventsBandCaption'),
      columns: store.state.statuses.map((status) => {
        const property = `statusEventMap[${status.id}].occurredAt`;
        const caption = translateValue('status', 'name', status.name);
        const columnOptions = {
          caption,
          ...(resolve(options.columns, status) || {}),
        };

        return buildColumn('statusEvent', property, columnOptions);
      }),
    }),
  },
  missingLocale: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    namespace: {
      dataType: 'string',
    },
    key: {
      dataType: 'string',
    },
    lastRequestAt: {
      dataType: 'datetime',
    },
    requestCount: {
      dataType: 'number',
    },
  },
  setElement: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    set: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    name: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
  },
  shipment: {
    acceptanceNumber: {
      dataType: 'number',
    },
    customer: {
      dataType: 'string',
    },
    deliveryDate: {
      dataType: 'date',
    },
    destinationCountryCode: {
      dataType: 'string',
    },
    orderEid: {
      dataType: 'number',
    },
  },
  status: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    name: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    position: {
      dataType: 'number',
      validationRules: [
        { type: 'required' },
      ],
    },
    restricted: {
      dataType: 'boolean',
      allowEditing: false,
    },
    station: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
  },
  statusEvent: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    occurredAt: {
      dataType: 'date',
      caption: ({ options, captionContext }) => {
        if (options && options.status && options.caption === 'name') {
          return i18next.t(`entities.status.values.name.${options.status.name}`);
        }

        return translateProperty('statusEvent', 'occurredAt', { context: captionContext });
      },
      editorOptions: {
        showClearButton: true,
      },
    },
  },
  user: {
    id: {
      dataType: 'number',
      allowEditing: false,
    },
    username: {
      dataType: 'string',
      validationRules: [
        { type: 'required' },
        { type: 'stringLength', min: 1 },
      ],
    },
    password: {
      dataType: 'string',
      validationRules: [
        {
          type: 'custom',
          validationCallback({ value, rule, data }) {
            const isUpdate = !!data.id;
            const caption = translateProperty('user', 'password');

            // ignore blank password on update
            if (isUpdate && !value) return true;

            // required
            if (!value) {
              // eslint-disable-next-line no-param-reassign
              rule.message = i18next.t('devExtreme.validation.required', { context: 'formatted' })
                .replace('{0}', caption);

              return false;
            }

            // string length
            if (value.length < 8 || value.length > 72) {
              // eslint-disable-next-line no-param-reassign
              rule.message = i18next.t('devExtreme.validation.stringLength', { context: 'formatted' })
                .replace('{0}', caption);

              return false;
            }

            return true;
          },
        },
      ],
    },
    role: {
      dataType: 'string',
      lookup: {
        dataSource: {
          store: {
            type: 'array',
            data: [
              { role: 'system-admin' },
              { role: 'system' },
              { role: 'manufacturing-admin' },
              { role: 'manufacturing-processor' },
              { role: 'order-manager' },
            ],
            key: 'role',
          },
        },
        valueExpr: 'role',
        displayExpr: 'role',
      },
    },
    manufacturerId: common.manufacturerId,
    'statusMap[:id]': common['statusMap[:id]'],
  },
});

export { columns };
